import { useState } from "react";
import { Mail, Eye, Send, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const TIER_BG = {
  Excellent: "bg-emerald-100 text-emerald-800",
  Strong: "bg-teal-100 text-teal-800",
  Acceptable: "bg-amber-100 text-amber-800",
  "Needs Improvement": "bg-orange-100 text-orange-800",
  "At Risk": "bg-red-600 text-white",
};

const TIER_OPTIONS = ["Excellent", "Strong", "Acceptable", "Needs Improvement", "At Risk"];

export default function TierEmailsPanel() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [selectedTiers, setSelectedTiers] = useState(new Set(TIER_OPTIONS));
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);

  const toggleTier = (tier) => {
    const next = new Set(selectedTiers);
    if (next.has(tier)) next.delete(tier); else next.add(tier);
    setSelectedTiers(next);
  };

  const payload = () => ({
    start_date: start,
    end_date: end,
    tier_filter: [...selectedTiers],
  });

  const runPreview = async () => {
    if (!start || !end) return toast.error("Pick a date range first");
    setLoading(true); setResults(null);
    try {
      const res = await api.post("/reports/tier-emails", { ...payload(), dry_run: true });
      setPreview(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  };

  const sendEmails = async () => {
    if (!start || !end) return toast.error("Pick a date range first");
    if (!window.confirm(`Send branded tier emails to ${preview?.summary?.with_email ?? "all eligible"} staff?`)) return;
    setLoading(true); setResults(null);
    try {
      const res = await api.post("/reports/tier-emails", { ...payload(), dry_run: false });
      setResults(res.data);
      const sent = (res.data.results || []).filter((r) => r.ok).length;
      const failed = (res.data.results || []).length - sent;
      toast.success(`Sent ${sent} email${sent === 1 ? "" : "s"}${failed ? `, ${failed} failed` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setLoading(false); }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="tier-emails-section">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-blue-600" />
        <h2 className="font-display text-lg font-bold">Tier Notification Emails</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">Send branded, tier-specific attendance emails via Resend. Staff with no email on record are skipped.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Start</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="tier-email-start" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">End</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="tier-email-end" />
        </label>
      </div>

      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-2">Send to these tiers</div>
        <div className="flex flex-wrap gap-2">
          {TIER_OPTIONS.map((tier) => {
            const on = selectedTiers.has(tier);
            return (
              <button
                key={tier}
                onClick={() => toggleTier(tier)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${on ? TIER_BG[tier] + " ring-2 ring-offset-2 ring-blue-400" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                data-testid={`tier-toggle-${tier.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {tier}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={runPreview} disabled={loading || !start || !end} className="btn-touch inline-flex items-center gap-1 px-4 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold disabled:opacity-40 text-sm" data-testid="tier-email-preview">
          <Eye className="w-4 h-4" /> Preview Recipients
        </button>
        <button onClick={sendEmails} disabled={loading || !preview?.summary?.with_email} className="btn-touch inline-flex items-center gap-1 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold disabled:opacity-40 text-sm" data-testid="tier-email-send">
          <Send className="w-4 h-4" /> Send Emails
        </button>
      </div>

      {preview && !results && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="tier-email-preview-panel">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <Stat label="Considered" value={preview.summary?.considered ?? 0} />
            <Stat label="Matched Filter" value={preview.summary?.matched_filter ?? 0} color="text-blue-700" />
            <Stat label="With Email" value={preview.summary?.with_email ?? 0} color="text-emerald-700" />
            <Stat label="Missing Email" value={preview.summary?.without_email ?? 0} color="text-amber-700" />
          </div>
          {preview.preview?.length > 0 ? (
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500">Name</th>
                    <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500">Email</th>
                    <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500">Tier</th>
                    <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500">Att%</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-semibold text-slate-900">{r.name || "—"}</td>
                      <td className="px-3 py-1.5 text-xs font-mono text-slate-600">{r.email || <span className="text-amber-600">no email</span>}</td>
                      <td className="px-3 py-1.5"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${TIER_BG[r.tier] || ""}`}>{r.tier}</span></td>
                      <td className="px-3 py-1.5 tabular-nums text-xs">{r.stats?.attendance_pct ?? 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="text-sm text-slate-500">No staff match the selected filter.</div>}
        </div>
      )}

      {results && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-emerald-50 p-4" data-testid="tier-email-results">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="font-semibold text-emerald-900">Emails sent</div>
          </div>
          <div className="text-sm text-slate-700">
            {(results.results || []).filter((r) => r.ok).length} delivered to Resend.
            {(results.results || []).filter((r) => !r.ok).length > 0 && (
              <span className="ml-2 text-red-700 font-semibold">{(results.results || []).filter((r) => !r.ok).length} failed.</span>
            )}
          </div>
          {(results.results || []).some((r) => !r.ok) && (
            <div className="mt-2 text-xs text-slate-600">
              <div className="flex items-center gap-1 font-semibold text-red-700 mb-1"><AlertCircle className="w-3 h-3" /> Failures:</div>
              <ul className="list-disc ml-5">
                {(results.results || []).filter((r) => !r.ok).slice(0, 10).map((r, i) => (
                  <li key={i}>{r.to || "(no email)"} — {r.error || "unknown"}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, color = "text-slate-900" }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-2.5 text-center">
      <div className={`font-display text-xl font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">{label}</div>
    </div>
  );
}
